<template>
    <div class="position-relative" style="width: 100%">
        <v-btn block color="primary" :disabled="disabled || isActivating" @click="activateCamera" aria-label="Take photo">
            <v-icon class="mr-2">mdi-camera</v-icon>
            TAKE PHOTO
        </v-btn>

        <!-- Full size image display -->
        <div v-if="capturedImage" class="captured-image-container">
            <img :src="capturedImage" class="captured-image" alt="Captured photo" />
            <div class="image-actions">
                <v-btn outlined @click="downloadImage" color="primary">
                    <v-icon class="mr-2">mdi-download</v-icon>
                    Download
                </v-btn>
            </div>
        </div>

        <!-- Camera fullscreen interface -->
        <div v-if="cameraActive" class="camera-fullscreen-overlay">
            <div class="camera-container">
                <video v-show="!isReviewing" ref="videoElement" autoplay playsinline class="video-element"></video>
                <div v-show="isReviewing" class="preview-container">
                    <img :src="currentCapturedImage" class="preview-image" alt="Photo preview" />
                </div>
            </div>

            <canvas ref="canvasElement" class="d-none"></canvas>
            
            <v-btn @click="closeCamera" color="rgba(0, 0, 0, 0.5)" class="close-camera-btn">
                <v-icon size="x-large" color="white">mdi-close</v-icon>
            </v-btn>

            <div v-if="!isReviewing" class="capture-button-container">
                <v-btn color="white" :disabled="isCapturing" @click="capturePhoto" class="capture-btn" x-large icon>
                    <v-icon size="32">mdi-camera</v-icon>
                </v-btn>
            </div>

            <div v-else class="review-controls">
                <v-btn outlined @click="discardPhoto" color="white" class="review-btn">
                    <v-icon class="mr-2">mdi-delete</v-icon>
                    DISCARD
                </v-btn>

                <v-btn outlined @click="savePhoto" color="white" class="review-btn">
                    <v-icon class="mr-2">mdi-check</v-icon>
                    ADD PHOTO
                </v-btn>
            </div>

            <div v-if="isCapturing || isActivating" class="progress-container">
                <v-progress-linear indeterminate color="primary"></v-progress-linear>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'CameraComponent',
    props: {
        capturedImage: { 
            type: String, 
            default: '' 
        },
        disabled: { 
            type: Boolean, 
            default: false 
        },
    },
    data() {
        return {
            mediaStream: null,
            cameraActive: false,
            isActivating: false,
            isCapturing: false,
            isReviewing: false,
            facingMode: 'environment',
            currentCapturedImage: '',
            selectedImages: []
        };
    },
    watch: {
        disabled(newValue) {
            if (newValue && this.cameraActive) {
                this.closeCamera();
            }
        },
        async selectedImages(files) {
            if (!files || files.length === 0) return;
            
            // Only take the first file since we only want one image
            const file = files[0];
            try {
                const base64Image = await this.fileToBase64(file);
                this.$emit('update:capturedImage', base64Image);
            } catch (error) {
                console.error('Error converting file to base64:', error);
                alert('Error processing selected image');
            }
            
            // Clear the selected files after processing
            this.selectedImages = [];
        }
    },
    mounted() {
        window.addEventListener('resize', this.onWindowResize);
        window.addEventListener('keydown', this.handleEscapeKey);
    },
    beforeDestroy() {
        this.cleanup();
        window.removeEventListener('resize', this.onWindowResize);
        window.removeEventListener('keydown', this.handleEscapeKey);
        document.body.classList.remove('camera-active');
    },
    methods: {
        fileToBase64(file) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader()
            
                reader.onload = () => {
                    if (reader.result) {
                        resolve(reader.result)
                    } else {
                        reject(new Error("FileReader result is null"))
                    }
                }
            
                reader.onerror = (error) => {
                    reject(error)
                }
            
                reader.readAsDataURL(file)
            })
        },

        async activateCamera() {
            if (this.disabled || this.isActivating) {
                return;
            }
            this.isActivating = true;

            try {
                this.cameraActive = true;
                this.isReviewing = false;
                await this.$nextTick();
                await this.initCamera();
                document.body.classList.add('camera-active');
            } catch (error) {
                console.error('Failed to activate camera:', error);
                this.cameraActive = false;
                alert('Unable to access camera. Please check permissions and try again.');
            }
            this.isActivating = false;
        },

        async initCamera() {
            try {
                if (!navigator.mediaDevices?.getUserMedia) {
                    alert('Camera access not supported by this browser');
                    return;
                }

                this.cleanup();

                const constraints = {
                    video: {
                        facingMode: this.facingMode,
                        width: { ideal: 1920 },
                        height: { ideal: 1080 }
                    },
                    audio: false
                };

                this.mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
                if (!this.$refs.videoElement) { 
                    throw new Error('Video element reference is null'); 
                }
                this.$refs.videoElement.srcObject = this.mediaStream;

                return new Promise((resolve) => {
                    if (!this.$refs.videoElement) {
                        resolve();
                        return;
                    }

                    this.$refs.videoElement.onloadedmetadata = async () => {
                        if (!this.$refs.videoElement || !this.$refs.canvasElement) {
                            resolve();
                            return;
                        }

                        this.$refs.canvasElement.width = this.$refs.videoElement.videoWidth;
                        this.$refs.canvasElement.height = this.$refs.videoElement.videoHeight;

                        try {
                            await this.$refs.videoElement.play();
                        } catch (error) {
                            console.error('Error playing video:', error);
                        }
                        resolve();
                    };
                });
            } catch (error) {
                console.error('Camera initialization error:', error);
                alert('Unable to access camera. Please ensure you have given permission and no other app is using the camera.');
            }
        },

        closeCamera() {
            this.cleanup();
            this.cameraActive = false;
            this.isReviewing = false;
            document.body.classList.remove('camera-active');
        },

        async capturePhoto() {
            if (!this.$refs.videoElement || !this.$refs.canvasElement || this.disabled || this.isCapturing) {
                return;
            }
            this.isCapturing = true;

            try {
                const video = this.$refs.videoElement;
                const canvas = this.$refs.canvasElement;

                if (video.videoWidth === 0 || video.videoHeight === 0) {
                    throw new Error('Invalid video dimensions');
                }

                canvas.width = video.videoWidth;
                canvas.height = video.videoHeight;

                const context = canvas.getContext('2d');
                if (!context) {
                    throw new Error('Could not get canvas context');
                }

                const videoRect = video.getBoundingClientRect();
                context.drawImage(video, 0, 0, canvas.width, canvas.height);

                if (this.isMobile() && videoRect.width / videoRect.height !== video.videoWidth / video.videoHeight) {
                    this.currentCapturedImage = this.cropImageToVisibleArea(canvas, video, videoRect);
                } else {
                    this.currentCapturedImage = canvas.toDataURL('image/jpeg', 0.9);
                }

                this.isReviewing = true;
                this.createFlashEffect();
            } catch (error) {
                console.error('Error capturing photo:', error);
                alert('Failed to capture photo. Please try again.');
            }
            this.isCapturing = false;
        },

        async discardPhoto() {
            this.currentCapturedImage = '';
            this.isReviewing = false;
            await this.$nextTick();

            if (this.$refs.videoElement && !this.$refs.videoElement.srcObject) {
                await this.initCamera();
            } else if (this.$refs.videoElement) {
                try {
                    await this.$refs.videoElement.play();
                } catch (error) {
                    console.error('Error playing video after discarding:', error);
                }
            }
        },

        async savePhoto() {
            if (!this.currentCapturedImage) {
                return;
            }
            
            this.$emit('update:capturedImage', this.currentCapturedImage);
            
            this.currentCapturedImage = '';
            this.isReviewing = false;
            this.closeCamera();
        },

        deleteImage() {
            this.$emit('update:capturedImage', '');
        },

        downloadImage() {
            if (!this.capturedImage) {
                return;
            }
            const link = document.createElement('a');
            link.href = this.capturedImage;
            link.download = `photo.jpg`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        },

        cropImageToVisibleArea(canvas, video, videoRect) {
            const displayAspect = videoRect.width / videoRect.height;
            const videoAspect = video.videoWidth / video.videoHeight;
            let cropX = 0, cropY = 0, cropWidth = canvas.width, cropHeight = canvas.height;

            if (displayAspect < videoAspect) {
                cropWidth = video.videoHeight * displayAspect;
                cropX = (video.videoWidth - cropWidth) / 2;
            } else {
                cropHeight = video.videoWidth / displayAspect;
                cropY = (video.videoHeight - cropHeight) / 2;
            }

            const cropCanvas = document.createElement('canvas');
            cropCanvas.width = cropWidth;
            cropCanvas.height = cropHeight;

            const cropContext = cropCanvas.getContext('2d');
            if (!cropContext) {
                return canvas.toDataURL('image/jpeg', 0.9);
            }

            cropContext.drawImage(canvas, cropX, cropY, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight);
            return cropCanvas.toDataURL('image/jpeg', 0.9);
        },

        isMobile() {
            return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        },

        createFlashEffect() {
            const flashElement = document.createElement('div');
            flashElement.className = 'camera-flash';
            document.body.appendChild(flashElement);
            flashElement.addEventListener('animationend', () => flashElement.remove());
        },

        cleanup() {
            if (this.mediaStream) {
                this.mediaStream.getTracks().forEach(track => track.stop());
                this.mediaStream = null;
            }
            if (this.$refs.videoElement) {
                this.$refs.videoElement.srcObject = null;
            }
        },

        handleEscapeKey(event) {
            if (event.key === 'Escape') {
                if (this.cameraActive) this.closeCamera();
            }
        },

        onWindowResize() {
            if (this.$refs.videoElement && this.$refs.canvasElement && this.$refs.videoElement.videoWidth > 0) {
                this.$refs.canvasElement.width = this.$refs.videoElement.videoWidth;
                this.$refs.canvasElement.height = this.$refs.videoElement.videoHeight;
            }
        }
    }
};
</script>

<style scoped>
.captured-image-container {
    margin-top: 16px;
    margin-bottom: 16px;
}

.captured-image {
    width: 100%;
    max-height: 400px;
    object-fit: contain;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
}

.image-actions {
    margin-top: 12px;
    display: flex;
    justify-content: center;
    gap: 12px;
    flex-wrap: wrap;
}

.camera-fullscreen-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100dvw;
    height: 100dvh;
    background-color: #000;
    z-index: 9999;
    display: flex;
    justify-content: center;
    align-items: center;
}

.camera-container {
    width: 100%;
    height: 100dvh;
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
    position: relative;
}

.video-element {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.preview-container {
    width: 100%;
    height: 100dvh;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: #000;
}

.preview-image {
    width: 100%;
    height: 100%;
    object-fit: contain;
}

.close-camera-btn {
    position: absolute;
    top: 16px;
    right: 20px;
    min-width: 0;
}

.capture-button-container {
    position: absolute;
    bottom: 32px;
    display: flex;
}

.capture-btn {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background-color: white;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.review-controls {
    position: absolute;
    bottom: 32px;
    gap: 50px;
    display: flex;
    justify-content: space-around;
    align-items: center;
}

.review-btn {
    background-color: rgba(0, 0, 0, 0.6) !important;
    min-width: 0;
}

.progress-container {
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
}

@media (max-width: 768px) {
    .capture-btn { 
        width: 64px; 
        height: 64px; 
    }
}
</style>